import LazoErrorService from "@/services/LazoErrorService";
import { claimChunkReload, isChunkLoadError } from "@/utils/chunkLoadRecovery";
import { hardReload } from "@/utils/hardNavigate";
import React from "react";
import { ErrorComponent } from "./ErrorComponent";

interface ErrorBoundaryState {
  hasError: boolean;
  /** A chunk failed to load and a reload is on its way: render nothing rather than flash the error page. */
  recovering: boolean;
}

export interface ErrorDetails {
  message: string;
  stack: string;
  componentStack: string;
  time: string;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onGoBack: () => void;
}

/** Upper bound on how long the reload waits for the error report to leave the browser. */
export const RELOAD_GRACE_MS = 1500;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, recovering: false };
  }

  static getDerivedStateFromError(_: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Error caught in ErrorBoundary:", error, errorInfo);
    const reported = this.logErrorToService(error, errorInfo);

    // A chunk that failed to download is not a bug in the page — it is one lost request. The user
    // would refresh; refresh for them, once per page (see chunkLoadRecovery for the loop guard).
    if (isChunkLoadError(error) && claimChunkReload(window.location.pathname)) {
      this.setState({ recovering: true });
      // Let the report leave first so the failure stays visible in the backend log, but never hold
      // the user hostage to a slow /api/log-error.
      Promise.race([reported, new Promise<void>((resolve) => setTimeout(resolve, RELOAD_GRACE_MS))]).then(() => hardReload());
    }
  }

  handleGoBack = () => {
    this.setState({ hasError: false, recovering: false });
    this.props.onGoBack();
  };

  //This function logs the error in backend to have more tracing about it
  logErrorToService(error: Error, errorInfo: React.ErrorInfo): Promise<void> {
    const errorDetails: ErrorDetails = {
      message: error.message,
      stack: secondLine(error.stack),
      componentStack: secondLine(errorInfo.componentStack),
      time: new Date().toISOString(),
    };

    return LazoErrorService.logError(errorDetails)
      .then(() => undefined)
      .catch((loggingError) => {
        console.error("Error while logging to service:", loggingError);
      });
  }

  render() {
    if (this.state.recovering) {
      return null;
    }
    if (this.state.hasError) {
      return <ErrorComponent goBack={this.handleGoBack} />;
    }

    return this.props.children;
  }
}

/** The first frame of a stack, or "" — an error without one must not crash the boundary itself. */
function secondLine(stack: string | null | undefined): string {
  return (stack ?? "").split("\n")[1]?.trim() ?? "";
}

export default ErrorBoundary;
